'use strict'
import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { CircularProgress } from '@material-ui/core'

const formatDays = (days = []) => days.map(d => d.slice(0, 3)).join(', ')

/**
 * Shows which properties' Aspire schedules do NOT yet reflect the scenario —
 * publish is manual in Aspire, so this is the worklist: OpportunityIDs to
 * update, first date needing the change, and the new day/week/route.
 */
export default function AspireDeltaModal ({ url, scenarioId, open, onClose, onSnackbar }) {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  const fetchDelta = useCallback(async () => {
    if (!scenarioId) return
    setLoading(true)
    setError(null)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios/${scenarioId}/aspire-delta`)
      setData(response.data)
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load Aspire delta')
    } finally {
      setLoading(false)
    }
  }, [url, scenarioId])

  useEffect(() => {
    if (open) fetchDelta()
    else setData(null)
  }, [open, fetchDelta])

  const copyCsv = async () => {
    try {
      await navigator.clipboard.writeText(data?.csv || '')
      onSnackbar('CSV copied to clipboard', 'success')
    } catch (err) {
      onSnackbar('Failed to copy CSV', 'error')
    }
  }

  const downloadCsv = () => {
    const blob = new Blob([data?.csv || ''], { type: 'text/csv' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `aspire-delta-${data?.scenarioName || scenarioId}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  if (!open) return null

  const rows = data?.rows || []

  return (
    <div
      onClick={(event) => { if (event.target === event.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1300,
        background: 'rgba(28,36,25,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24
      }}
    >
      <div style={{
        width: 'min(1080px, 94vw)', maxHeight: '86vh',
        background: 'var(--df-bg)', border: '1px solid var(--df-line)', borderRadius: 10,
        boxShadow: '0 18px 50px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid var(--df-line)', background: 'var(--df-surface)',
          display: 'flex', alignItems: 'center', gap: 10
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--df-ink)' }}>Aspire Update Worklist</div>
            <div style={{ fontSize: 11, color: 'var(--df-ink-4)', marginTop: 2 }}>
              Upcoming Aspire visits that don't match this scenario yet — update these opportunities manually in Aspire.
              {data && ` Looking ahead ${data.lookaheadDays} days.`}
            </div>
          </div>
          {data && (
            <span style={{
              fontSize: 12, fontWeight: 800, padding: '4px 10px', borderRadius: 99,
              background: rows.length ? 'rgba(169,52,29,0.1)' : 'rgba(31,94,58,0.1)',
              color: rows.length ? '#a9341d' : 'var(--df-primary)'
            }}>
              {rows.length ? `${rows.length} pending` : 'All in sync'}
            </span>
          )}
          <button className="btn btn-ghost" style={{ fontSize: 12, padding: '6px 10px' }} onClick={copyCsv} disabled={!rows.length}>
            Copy CSV
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 12, padding: '6px 10px' }} onClick={downloadCsv} disabled={!rows.length}>
            Download
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 14, padding: '6px 10px' }} onClick={onClose} title="Close">
            ×
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
              <CircularProgress size={28} style={{ color: 'var(--df-primary)' }} />
            </div>
          )}
          {error && (
            <div style={{ padding: 24, color: '#a9341d', fontSize: 13 }}>{error}</div>
          )}
          {!loading && !error && data && !rows.length && (
            <div style={{ padding: 36, textAlign: 'center', color: 'var(--df-ink-3)', fontSize: 13 }}>
              Every upcoming Aspire visit matches this scenario. Nothing to update. ✓
            </div>
          )}
          {!loading && !error && rows.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--df-surface)', position: 'sticky', top: 0 }}>
                  {['Opportunity', 'Property', 'First change', 'Currently in Aspire', 'Change to', 'Why', 'Visits'].map(label => (
                    <th key={label} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 10, textTransform: 'uppercase', color: 'var(--df-ink-4)', borderBottom: '1px solid var(--df-line)', whiteSpace: 'nowrap' }}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.aspirePropertyId} style={{ borderBottom: '1px solid var(--df-line)' }}>
                    <td style={{ padding: '8px 12px', fontFamily: 'var(--df-font-mono, monospace)', fontWeight: 700, color: 'var(--df-ink)' }}>
                      {row.opportunityIds.join(', ') || '—'}
                    </td>
                    <td style={{ padding: '8px 12px', maxWidth: 260 }}>
                      <div style={{ fontWeight: 700, color: 'var(--df-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.propertyName}</div>
                      <div style={{ fontSize: 10, color: 'var(--df-ink-4)' }}>#{row.aspirePropertyId}{row.movedInScenario ? ' · moved in scenario' : ''}</div>
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'var(--df-font-mono, monospace)', whiteSpace: 'nowrap' }}>
                      {row.firstChangeDate}
                      {row.firstChangeTicket && <div style={{ fontSize: 10, color: 'var(--df-ink-4)' }}>WT {row.firstChangeTicket}</div>}
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--df-ink-3)' }}>
                      {row.currentDay}{row.currentRouteName ? ` · ${row.currentRouteName}` : ''}
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--df-primary)' }}>
                      {formatDays(row.newDays) || '—'}{row.newWeekType ? ` · Wk ${row.newWeekType}` : ''}
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--df-ink-3)' }}>{row.newRouteName || row.newCrewName || ''}</div>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {row.mismatchReasons.map(reason => (
                        <span key={reason} className="rw-pill" style={{ marginRight: 4, padding: '1px 6px', fontSize: 9, background: 'rgba(169,52,29,0.1)', color: '#a9341d' }}>{reason}</span>
                      ))}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--df-font-mono, monospace)' }}>{row.pendingVisitCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
